// src/components/mitchRoom/revenueAnalytics/AssumptionsPanel.js
//
// What the forecast believes, with the evidence count behind each number,
// plus three what-if sliders. The sliders recompute CLIENT-SIDE from the
// exposed component vectors (see applyScenario) — instant, never persisted,
// and always labelled "your scenario".
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from 'components/ui/collapsible';
import { Slider } from 'components/ui/slider';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { formatPercent } from 'utils/format';

const AGE_BUCKETS = ['m1', 'm2', 'm3', 'm4-6', 'm7+'];
const AGE_LABELS = { m1: 'Month 1', m2: 'Month 2', m3: 'Month 3', 'm4-6': 'Months 4–6', 'm7+': 'Month 7+' };
const PHASES = ['aug', 'sep', 'oct-dec', 'jan-apr', 'may-jun', 'jul'];
const PHASE_LABELS = { aug: 'Aug', sep: 'Sep', 'oct-dec': 'Oct–Dec', 'jan-apr': 'Jan–Apr', 'may-jun': 'May–Jun', jul: 'Jul' };

export const DEFAULT_SCENARIO = { churn: 1, yoy: null, collect: null };

// Scale a month's components under a scenario. `h` is the pooled monthly
// hazard, `d` the number of months of survival the renewals in that month
// carry (0 for the current month's committed invoices, 1 for next month…).
export function applyScenario(m, scenario, assumptions, monthsAhead) {
  if (!m || !m.has_forecast) return m?.forecast ?? null;
  const pooled = assumptions?.retention_grid?.find((g) => g.age_bucket === 'all' && g.season_phase === 'all');
  const h = pooled?.hazard ?? 0.08;
  const baseCollect = assumptions?.p_collect_established?.value ?? 0.97;
  const baseYoy = assumptions?.yoy_factor?.value ?? 1;
  const c = scenario.churn ?? 1;
  const d = Math.max(1, monthsAhead + 1);
  const survivalRatio = Math.pow((1 - Math.min(0.95, c * h)) / (1 - h), d);
  const collectRatio = scenario.collect ? scenario.collect / baseCollect : 1;
  const yoyRatio = scenario.yoy ? scenario.yoy / baseYoy : 1;
  const renewals = (m.expected_renewals ?? 0) * survivalRatio * collectRatio;
  const nb = (m.new_business ?? 0) * yoyRatio;
  const tail = (m.tail_of_new ?? 0) * yoyRatio * survivalRatio;
  const leakRate = assumptions?.refund_rate?.value ?? 0.018;
  return (m.actual ?? 0) + renewals + nb + tail - leakRate * (renewals + nb + tail);
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-2">
      <p className="text-token-xs text-muted-foreground">{label}</p>
      <p className="text-token-md font-semibold">{value}</p>
      {sub && <p className="text-token-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function AssumptionsPanel({ assumptions, scenario, onScenarioChange }) {
  const [open, setOpen] = useState(false);
  if (!assumptions || !assumptions.retention_grid) return null;

  const grid = assumptions.retention_grid;
  const cell = (b, p) => grid.find((g) => g.age_bucket === b && g.season_phase === p);
  const pc = assumptions.p_collect_established?.value;
  const pcFirst = assumptions.p_collect_first_real_charge?.value;
  const yoy = assumptions.yoy_factor;
  const ann = assumptions.annual_renewal;
  const ref = assumptions.refund_rate;
  const pending = assumptions.pending_cancel_subs;
  const stale = assumptions.stale_schedule_subs;
  const s = scenario || DEFAULT_SCENARIO;
  const isDefault = (s.churn ?? 1) === 1 && !s.yoy && !s.collect;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full items-center justify-between text-left">
            <div>
              <h3 className="text-token-xl font-semibold">Assumptions &amp; what-if</h3>
              <p className="text-token-xs text-muted-foreground">
                Retention grid, collection rates, YoY factor and refund rate as fitted by the last
                pipeline run — each with the number of observations behind it.
              </p>
            </div>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 grid grid-cols-2 gap-2 xl:grid-cols-6">
            <Stat
              label="Collection (established)"
              value={pc !== undefined ? formatPercent(pc * 100, 0) : '—'}
              sub="P(invoice paid in month)"
            />
            <Stat
              label="Collection (first real charge)"
              value={pcFirst !== undefined ? formatPercent(pcFirst * 100, 0) : '—'}
              sub={`${assumptions.p_collect_first_real_charge?.n_obs ?? 0} subs awaiting one`}
            />
            <Stat
              label="YoY new-customer factor"
              value={yoy ? `×${yoy.value.toFixed(2)}` : '—'}
              sub={yoy ? `raw ×${(yoy.raw_value ?? 1).toFixed(2)} (${yoy.n_obs} vs ${yoy.n_events}), damped` : ''}
            />
            <Stat
              label="Annual renewal"
              value={ann ? formatPercent(ann.value * 100, 0) : '—'}
              sub={ann ? `${ann.n_events ?? 0}/${ann.n_obs ?? 0} at first anniversary` : ''}
            />
            <Stat
              label="Refund rate"
              value={ref ? formatPercent(ref.value * 100, 1) : '—'}
              sub={ref ? `${ref.n_obs ?? 0} refunds, trailing 12m` : ''}
            />
            <Stat
              label="Pending cancel / stale"
              value={`${pending?.value ?? 0} · ${stale?.value ?? 0}`}
              sub="excluded from committed"
            />
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-token-sm">
              <thead>
                <tr>
                  <th className="border-b border-borderGrey p-1.5 text-left font-semibold">Monthly churn hazard</th>
                  {PHASES.map((p) => (
                    <th key={p} className="border-b border-borderGrey p-1.5 text-right font-semibold">
                      {PHASE_LABELS[p]}
                    </th>
                  ))}
                  <th className="border-b border-borderGrey p-1.5 text-right font-semibold">All phases</th>
                </tr>
              </thead>
              <tbody>
                {AGE_BUCKETS.map((b) => (
                  <tr key={b}>
                    <td className="border-b border-borderGrey p-1.5">{AGE_LABELS[b]}</td>
                    {PHASES.map((p) => {
                      const c = cell(b, p);
                      return (
                        <td
                          key={p}
                          className="border-b border-borderGrey p-1.5 text-right tabular-nums"
                          title={c ? `raw ${c.raw_hazard !== null && c.raw_hazard !== undefined ? formatPercent(c.raw_hazard * 100, 1) : '—'} · ${c.n_events}/${c.n_obs} sub-months · shrunk toward ${formatPercent((c.shrunk_from ?? 0) * 100, 1)}` : ''}
                        >
                          {c ? formatPercent(c.hazard * 100, 1) : '—'}
                          {c && c.n_obs < 30 && <span className="ml-0.5 text-muted-foreground">*</span>}
                        </td>
                      );
                    })}
                    <td className="border-b border-borderGrey p-1.5 text-right font-medium tabular-nums">
                      {cell(b, 'all') ? formatPercent(cell(b, 'all').hazard * 100, 1) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-1 text-token-xs text-muted-foreground">
              P(a monthly sub lapses in the month) by age of subscription × season phase, recency-weighted
              (12-month half-life) and shrunk toward the age-only rate where a cell is thin (* = under 30
              sub-months). Hover a cell for the raw rate and count.
            </p>
          </div>

          <div className="mt-4 rounded-token-large border border-dashed border-borderGrey p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-token-md font-semibold">Your scenario (client-side, not saved)</p>
              <Button
                variant="ghost"
                size="sm"
                disabled={isDefault}
                onClick={() => onScenarioChange?.(DEFAULT_SCENARIO)}
              >
                <RotateCcw size={13} /> Reset
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label className="text-token-sm">
                  Churn ×{(s.churn ?? 1).toFixed(2)}{' '}
                  <span className="text-muted-foreground">(0.7 – 1.3)</span>
                </Label>
                <Slider
                  className="mt-2"
                  min={0.7}
                  max={1.3}
                  step={0.05}
                  value={[s.churn ?? 1]}
                  onValueChange={([v]) => onScenarioChange?.({ ...s, churn: v })}
                />
              </div>
              <div>
                <Label className="text-token-sm">
                  YoY factor ×{(s.yoy ?? yoy?.value ?? 1).toFixed(2)}{' '}
                  <span className="text-muted-foreground">(model ×{(yoy?.value ?? 1).toFixed(2)})</span>
                </Label>
                <Slider
                  className="mt-2"
                  min={0.5}
                  max={2.5}
                  step={0.05}
                  value={[s.yoy ?? yoy?.value ?? 1]}
                  onValueChange={([v]) => onScenarioChange?.({ ...s, yoy: v })}
                />
              </div>
              <div>
                <Label className="text-token-sm">
                  Collection {formatPercent((s.collect ?? pc ?? 0.97) * 100, 0)}{' '}
                  <span className="text-muted-foreground">(model {formatPercent((pc ?? 0.97) * 100, 0)})</span>
                </Label>
                <Slider
                  className="mt-2"
                  min={0.8}
                  max={1}
                  step={0.01}
                  value={[s.collect ?? pc ?? 0.97]}
                  onValueChange={([v]) => onScenarioChange?.({ ...s, collect: v })}
                />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
